/**
 * Post-session results for the facilitator. Spec 11.3: "Each participant's path, squares
 * visited, arrows/snakes, duration, opted-in reflections."
 *
 * The privacy rule is the important part of this screen, and it is enforced on the server:
 * `reflection.listForParticipant` returns text to the host only for participants who set
 * `share_reflections` (spec 14.8, spec 29.4 decision 5). This screen asks per participant
 * and shows what comes back — it never assumes, and it says plainly that reflections are
 * private by default so a facilitator does not read the absence as a bug.
 */
import type { ParticipantDto, ReflectionDto } from '@lila/shared';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { errorKeyFor } from '../../../lib/errors';
import { trpcClient } from '../../../lib/trpc';

export const Route = createFileRoute('/$lang/studio/results/$id')({ component: Page });

function Page(): ReactNode {
  const { t } = useTranslation(['studio', 'board', 'common']);
  const { id } = Route.useParams();

  const session = useQuery({
    queryKey: ['session', id] as const,
    queryFn: () => trpcClient.session.get.query({ sessionId: id }),
  });

  const participants = session.data?.participants ?? [];

  /*
   * One query per participant. `session.results` is written from the caller's own point of
   * view, and the host needs every journey — so the fan-out is real rather than a shape we
   * could flatten server-side without changing what the procedure means.
   */
  const journeys = useQueries({
    queries: participants.map((participant) => ({
      queryKey: ['session', id, 'results', participant.id] as const,
      queryFn: () => trpcClient.session.results.query({ sessionId: id }),
      enabled: session.data !== undefined,
    })),
  });

  const reflections = useQueries({
    queries: participants.map((participant) => ({
      queryKey: ['reflection', id, participant.id] as const,
      queryFn: () =>
        trpcClient.reflection.listForParticipant.query({
          sessionId: id,
          participantId: participant.id,
        }),
      enabled: session.data !== undefined,
      /*
       * A participant who has not opted in produces an empty list rather than an error, so
       * a failure here is a real failure and worth surfacing.
       */
      retry: false,
    })),
  });

  if (session.error !== null) {
    return <Alert tone="error">{t(errorKeyFor(session.error))}</Alert>;
  }

  if (session.isLoading) {
    return <SkeletonList rows={4} label={t('studio:results.title')} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-ink">{t('studio:results.title')}</h1>
        <p className="text-sm text-ink-muted">{session.data?.title ?? ''}</p>
      </header>

      {/*
       * Said out loud, before a facilitator wonders why most cards are empty. Spec 11.5:
       * "Reflections are never subject to moderation review. They are private personal
       * writing."
       */}
      <Alert tone="info">{t('studio:results.private')}</Alert>

      {participants.length === 0 ? (
        <EmptyState title={t('studio:session.lobbyEmpty')} />
      ) : (
        <ul className="flex flex-col gap-4">
          {participants.map((participant, index) => (
            <li key={participant.id}>
              <ParticipantResults
                participant={participant}
                summary={journeys[index]?.data}
                reflections={reflections[index]?.data ?? []}
                loading={journeys[index]?.isLoading ?? true}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ParticipantResults({
  participant,
  summary,
  reflections,
  loading,
}: {
  readonly participant: ParticipantDto;
  readonly summary:
    | {
        readonly squaresVisited: number;
        readonly arrowsAscended: number;
        readonly snakesDescended: number;
        readonly durationMs: number | null;
        readonly path: readonly number[];
      }
    | undefined;
  readonly reflections: readonly ReflectionDto[];
  readonly loading: boolean;
}): ReactNode {
  const { t } = useTranslation(['studio', 'board']);

  /* Only reflections whose text actually came back — the rest were not shared. */
  const shared = reflections.filter((reflection) => reflection.userText !== null);

  return (
    <Card title={participant.displayName}>
      {loading || summary === undefined ? (
        <SkeletonList rows={2} label={participant.displayName} />
      ) : (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Figure label={t('studio:results.squares')} value={summary.squaresVisited} />
            <Figure label={t('studio:results.arrows')} value={summary.arrowsAscended} />
            <Figure label={t('studio:results.snakes')} value={summary.snakesDescended} />
            {summary.durationMs !== null && (
              <Figure
                label={t('studio:results.duration')}
                value={Math.round(summary.durationMs / 60_000)}
              />
            )}
          </dl>

          <p className="text-xs leading-relaxed tabular-nums text-ink-muted">
            {summary.path.join(' → ')}
          </p>

          <section>
            <h3 className="mb-1 text-sm font-medium text-ink">{t('studio:results.reflections')}</h3>
            {shared.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('studio:results.noReflections')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {shared.map((reflection) => (
                  <li key={reflection.id} className="rounded-md border border-border px-3 py-2">
                    <p className="text-xs text-ink-muted">
                      {t('board:square.number', {
                        defaultValue: '{{number}}',
                        number: reflection.squareNumber,
                      })}
                    </p>
                    <p className="text-sm text-ink">{reflection.userText}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Card>
  );
}

function Figure({ label, value }: { readonly label: string; readonly value: number }): ReactNode {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}
